import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {SplashLoaderModule} from "../splash-loader/splash-loader.module";
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {PreviewContainerLoaderComponent} from "./preview-container-loader/preview-container-loader.component";
import {AsyncPipe} from "@angular/common";
import {validate, v4} from "uuid";
import {IReceiverMessage} from "../../receiver-communication/messages/i-receiver-message";
import {sessionStateServiceInjectionToken} from "../../global-states/session/session-state-service-injection-token";
import {ISessionStateService} from "../../global-states/session/i-session-state.service";
import {IReceiver} from "../../models/i-receiver";
import {AppRestClient} from "../../rest-clients/application/app/app-rest.client";
import {HttpClient} from '@angular/common/http';
import {
  IApp,
  IAppRestClient,
  IGetInstalledApplicationPageForm,
  ILauncher,
  ILauncherRestClient,
  launcherRestClientInjectionToken
} from '@zaeper/angular-application-store-lib';
import {IPage} from "@zaeper/communication-lib";
import {IPageMessage} from "../../receiver-communication/messages/i-page-message";
import {HostUtil} from "../../utils/host-util";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-preview-container',
  templateUrl: './preview-container.component.html',
  providers: [SplashLoaderModule],
  imports: [PreviewContainerLoaderComponent, AsyncPipe]
})
export class PreviewContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly launcherUrl$: Observable<SafeResourceUrl>;

  private static _PREVIEWER_IDENTIFIER: string = v4();
  private static _MESSAGE_PAYLOAD_DELIMITER: string = ".";
  private static _LOADER_FALLBACK_DELAY_MS: number = 3000;
  private static _APPS_PATH_SEGMENT: string = "/apps/";
  private static _LAUNCHERS_PATH: string = "apps/launchers";
  private static _THIRD_PARTY_APPS_PATH: string = "third-party-apps/apps";

  public readonly isLoaderVisible$: Observable<boolean>;
  private readonly _isLoaderVisible$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private readonly _launcherUrl$: BehaviorSubject<SafeResourceUrl>;
  private readonly _appRestClient: IAppRestClient;
  private readonly _boundMessageHandler = (event: MessageEvent) => this._messageHandler(event);
  private readonly _subscriptions: Subscription = new Subscription();
  private _installedApps: IApp[] = [];

  @ViewChild('previewerView', {read: ElementRef}) private readonly _previewerView!: ElementRef;

  constructor(
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(launcherRestClientInjectionToken) private readonly _launcherRestClient: ILauncherRestClient,
    readonly httpClient: HttpClient,
    private readonly _domSanitizer: DomSanitizer
  ) {
    this.isLoaderVisible$ = this._isLoaderVisible$.asObservable();
    this._appRestClient = new AppRestClient(httpClient);
    this._launcherUrl$ = new BehaviorSubject<SafeResourceUrl>(
      this._buildPreviewerUrl(null)
    );
    this.launcherUrl$ = this._launcherUrl$.asObservable();
  }

  ngOnInit(): void {
    this._subscriptions.add(this._sessionStateService.activeReceiver$.subscribe((activeReceiver: IReceiver | null) => {
      if (activeReceiver === null) {
        this._installedApps = [];
        this._launcherUrl$.next(this._buildPreviewerUrl(null));
        return;
      }
      this._cacheInstalledApps(activeReceiver);
      this._resolveLauncherPreviewerUrl(activeReceiver);
    }));
  }

  private _cacheInstalledApps(activeReceiver: IReceiver): void {
    const getInstalledAppsForm: IGetInstalledApplicationPageForm = {
      receiverId: activeReceiver.id,
      pageNumber: 0,
      pageSize: 100
    };
    this._appRestClient.getInstalledApplicationPage(getInstalledAppsForm).then((installedAppsPage: IPage<IApp>) => {
      this._installedApps = installedAppsPage.records;
      this._sendPreviewContext();
    }).catch(() => {
      this._installedApps = [];
    });
  }

  private _sendPreviewContext(): void {
    const iframe: HTMLIFrameElement | undefined = this._previewerView?.nativeElement;
    if (!iframe?.contentWindow) {
      return;
    }

    const appsBaseUrl: string | null = this._resolveAppsBaseUrl();
    const body = {
      identifier: "preview-context",
      appsBaseUrl,
      installedApps: this._installedApps.map((app: IApp) => ({
        id: app.id,
        identifier: app.identifier,
        latestVersion: app.latestVersion
      }))
    };

    const payload: string = [
      PreviewContainerComponent._PREVIEWER_IDENTIFIER,
      v4(),
      JSON.stringify(body)
    ].join(PreviewContainerComponent._MESSAGE_PAYLOAD_DELIMITER);

    iframe.contentWindow.postMessage(payload, "*");
  }

  private _resolveAppsBaseUrl(): string | null {
    const previewerUrl: string = HostUtil.getApplicationsPreviewerEndpointUrl();
    const appsSegmentIndex: number = previewerUrl.indexOf(PreviewContainerComponent._APPS_PATH_SEGMENT);
    if (appsSegmentIndex < 0) {
      return null;
    }
    return previewerUrl.substring(0, appsSegmentIndex + PreviewContainerComponent._APPS_PATH_SEGMENT.length);
  }

  private _resolveLauncherPreviewerUrl(activeReceiver: IReceiver): void {
    if (!activeReceiver.launcherId) {
      this._launcherUrl$.next(this._buildPreviewerUrl(null));
      return;
    }

    const getInstalledApplicationPageForm: IGetInstalledApplicationPageForm = {
      receiverId: activeReceiver.id,
      pageNumber: 0,
      pageSize: 100
    };

    this._launcherRestClient.getInstalledApplicationPage(getInstalledApplicationPageForm).then((page: IPage<ILauncher>) => {
      const launcher: ILauncher | undefined = page.records.find((record: ILauncher) => record.id === activeReceiver.launcherId);
      const launcherUrl: string | null = this._resolveLauncherHostedUrl(launcher ?? null);
      this._launcherUrl$.next(this._buildPreviewerUrl(launcherUrl));
    }).catch(() => {
      this._launcherUrl$.next(this._buildPreviewerUrl(null));
    });
  }

  private _resolveLauncherHostedUrl(launcher: ILauncher | null): string | null {
    if (!launcher || !launcher.identifier || !launcher.latestVersion) {
      return null;
    }
    const host: string | null = this._resolveApplicationsHostUrl();
    if (host === null) {
      return null;
    }
    return `${host}/${PreviewContainerComponent._LAUNCHERS_PATH}/${launcher.identifier}/${launcher.latestVersion}/`;
  }

  private _resolveAppHostedUrl(app: IApp): string | null {
    if (!app.identifier || !app.latestVersion) {
      return null;
    }
    const host: string | null = this._resolveApplicationsHostUrl();
    if (host === null) {
      return null;
    }
    return `${host}/${PreviewContainerComponent._THIRD_PARTY_APPS_PATH}/${app.identifier}/${app.latestVersion}/`;
  }

  private _resolveApplicationsHostUrl(): string | null {
    const previewerUrl: string = HostUtil.getApplicationsPreviewerEndpointUrl();
    const appsSegmentIndex: number = previewerUrl.indexOf(PreviewContainerComponent._APPS_PATH_SEGMENT);
    if (appsSegmentIndex < 0) {
      return null;
    }
    return previewerUrl.substring(0, appsSegmentIndex);
  }

  private _buildPreviewerUrl(launcherUrl: string | null): SafeResourceUrl {
    if (launcherUrl !== null) {
      return this._domSanitizer.bypassSecurityTrustResourceUrl(launcherUrl);
    }
    return this._domSanitizer.bypassSecurityTrustResourceUrl("about:blank");
  }

  ngAfterViewInit(): void {
    window.addEventListener('message', this._boundMessageHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this._boundMessageHandler);
    this._subscriptions.unsubscribe();
  }

  public onIframeLoad(): void {
    setTimeout(() => this._isLoaderVisible$.next(false), PreviewContainerComponent._LOADER_FALLBACK_DELAY_MS);
  }

  private async _messageHandler(messageEvent: MessageEvent): Promise<void> {
    const iframe: HTMLIFrameElement = this._previewerView.nativeElement;
    if (messageEvent.source !== iframe.contentWindow) {
      return;
    }
    const payloadChunks: string[] = (<string>messageEvent.data).split(PreviewContainerComponent._MESSAGE_PAYLOAD_DELIMITER);
    if (payloadChunks.length <= 2 || !validate(payloadChunks[0]) || !validate(payloadChunks[1])) {
      return;
    }
    const [, messageId, messageBody]: string[] = payloadChunks;
    const receiverMessage: IReceiverMessage = JSON.parse(messageBody);

    if (receiverMessage.identifier === "application-ready") {
      this._isLoaderVisible$.next(false);
      this._sendPreviewContext();
      return;
    }

    if (receiverMessage.identifier === "get-receiver-information") {
      const activeReceiver: IReceiver | null = this._sessionStateService.getActiveReceiver();
      if (activeReceiver !== null) {
        this._respond(iframe, messageId, activeReceiver);
      }
      return;
    }

    if (receiverMessage.identifier === "get-installed-apps") {
      const activeReceiver: IReceiver | null = this._sessionStateService.getActiveReceiver();
      if (activeReceiver !== null) {
        const getInstalledAppsForm: IGetInstalledApplicationPageForm = {
          receiverId: activeReceiver.id,
          pageNumber: 0,
          pageSize: 0
        };
        const installedAppsPage: IPage<IApp> = await this._appRestClient.getInstalledApplicationPage(getInstalledAppsForm);
        const installedAppsPageMessage: IPageMessage<IApp> = {
          records: installedAppsPage.records,
          totalRecords: installedAppsPage.totalRecords
        };
        this._respond(iframe, messageId, installedAppsPageMessage);
      }
      return;
    }

    if (receiverMessage.identifier === "open-app") {
      const appId: string | undefined = (receiverMessage as unknown as { appId?: string }).appId;
      const app: IApp | undefined = appId ? this._installedApps.find((candidate: IApp) => candidate.id === appId) : undefined;
      const appUrl: string | null = app ? this._resolveAppHostedUrl(app) : null;
      if (appUrl === null) {
        console.warn("[preview] open-app could not resolve URL", { appId, matched: app });
        return;
      }
      this._launcherUrl$.next(this._domSanitizer.bypassSecurityTrustResourceUrl(appUrl));
      return;
    }
  }

  private _respond(iframe: HTMLIFrameElement, messageId: string, body: unknown): void {
    const serialized: string = JSON.stringify(body);
    const payload: string = [PreviewContainerComponent._PREVIEWER_IDENTIFIER, messageId, serialized].join(PreviewContainerComponent._MESSAGE_PAYLOAD_DELIMITER);
    iframe.contentWindow?.postMessage(payload, "*");
  }
}
